<script setup lang="ts">
  import { useFormContext, getModelFromContext, setModelWithContext } from '@othercommerce/foundation';
  import { useId, computed } from 'vue';

  interface Option {
    label: string,
    value: any,
  }

  interface Props {
    id?: string | undefined;
    name?: string | undefined;
    label?: string | undefined;
    placeholder?: string | false;
    floating?: boolean;
    required?: boolean;
    readonly?: boolean;
    disabled?: boolean;
    options?: Option[] | Record<string, Option> | undefined;
  }

  defineOptions({
    inheritAttrs: false,
  });

  const props = withDefaults(defineProps<Props>(), {
    floating: true,
    required: false,
    readonly: false,
    disabled: false,
    options: () => [],
  });

  const context = useFormContext();
  const random = useId();

  const model = defineModel({
    get: (value) => getModelFromContext(props.name, context, value ?? null),
    set: (value) => setModelWithContext(props.name, context, value ?? null),
  });

  const id = computed(() => {
    return props.id ?? random;
  });

  // Selects behavior is incosistent for readonly attributes across browsers.
  // For workaround we treat both disabled and readonly atrributes as disabled.
  const disabled = computed(() => {
    return Boolean(props.disabled || props.readonly || (context && context.readonly.value));
  });

  const normalizedOptions = computed(() => {
    if (Array.isArray(props.options)) {
      return props.options;
    }

    return Object.values(props.options ?? {});
  });
</script>
<template>
  <SfControlWrapper v-bind="{ id, name, label, floating, required }" v-slot="{errorId}">
    <select class="control select" v-bind="{ id, name, required, disabled, ...$attrs }" v-model="model" :aria-describedby="errorId">
      <option key="required" selected :disabled="required" :value="null">
        {{ placeholder || (required ? $t('SHOP.COMMON.LABEL.CHOOSE_VALUE') : $t('SHOP.COMMON.LABEL.NO_VALUE')) }}
      </option>
      <option v-for="option in normalizedOptions" :key="option.value" :value="option.value">
        {{ option.label }}
      </option>
    </select>
  </SfControlWrapper>
</template>
<style lang="scss">
  :root {
    --sf-select-control-arrow: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23000000' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e");
  }
</style>
<style scoped lang="scss">
  .select.control {
    padding-right: calc(var(--sf-input-padding-right) + 2rem);
    background-image: var(--sf-select-control-arrow);
    background-repeat: no-repeat;
    background-position: right var(--sf-input-padding-right) center;
    background-size: 1rem;
    cursor: pointer;
    appearance: none;

    & + label {
      --sf-input-label-color: var(--sf-input-color);
    }

    option:checked {
      font-weight: var(--sf-select-checked-font-weight);
      background-color: var(--sf-select-checked-background);
    }
  }
</style>
